import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLazyGetFixedPricingLocationsQuery } from "@/redux/apis/config/trip/tripCost";
import { useAppSelector } from "@/redux/hooks";
import { fixedPricingLocationSelector, fixedPricingLocationsSelector } from "@/redux/slices/trip/selectors";
import { FixedPricingLocation } from "@/redux/slices/trip/types";
import { useFixedPricingContext } from "./fixedPricing";

interface FixedPricingLocationState {
  pricingLocations: FixedPricingLocation[];
  locationName: string | null;
  isFetching: boolean;
  isRestricted: boolean;
  pricingLocation?: FixedPricingLocation;
}

const FixedPricingLocationContext = createContext<FixedPricingLocationState | null>(null);

/**
 * @deprecated Dead code — superseded by `TripCostV2/.../ZoneRates/ZoneRatesContext`.
 *
 * Known bug, deliberately left unfixed: `getPricingLocations` is called without a `page_size`,
 * so it only ever receives the API's default 12 zones. That is the fault reported by Drivision
 * in 2026-09 and fixed in the V2 context. It cannot fire here because nothing mounts this
 * provider. If this tree is ever revived, port the V2 fix across before using it.
 */
export const FixedPricingLocationContextProvider = ({ children }: React.PropsWithChildren) => {
  const { isFetching: isFetchingPricingProfiles, pricingProfile } = useFixedPricingContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationName = searchParams.get("locationName");
  const { locationId = "" } = useParams();

  const [getPricingLocations, { isFetching: isFetchingPricingLocations, isSuccess: isSuccessPricingLocations }] =
    useLazyGetFixedPricingLocationsQuery();
  const pricingLocations = useAppSelector(fixedPricingLocationsSelector);
  const pricingLocation = useAppSelector(fixedPricingLocationSelector(locationId));
  const [isRestricted, setIsRestricted] = useState(false);

  useEffect(() => {
    if (!isFetchingPricingProfiles && pricingProfile) getPricingLocations({ id: pricingProfile.id });
  }, [isFetchingPricingProfiles, pricingProfile, getPricingLocations]);

  // breadcrumbs setup
  useEffect(() => {
    if (pricingLocation && !pricingLocation.managed && locationName !== pricingLocation.pickupLocation.name) {
      searchParams.set("locationName", pricingLocation.pickupLocation.name);
      setSearchParams(searchParams);
    }
  }, [pricingLocation, locationName, searchParams, setSearchParams]);

  // restricted check
  useEffect(() => {
    if (isSuccessPricingLocations && (!pricingLocation || pricingLocation.managed)) setIsRestricted(true);
  }, [isSuccessPricingLocations, pricingLocation]);

  return (
    <FixedPricingLocationContext.Provider
      value={{ pricingLocations, isFetching: isFetchingPricingLocations, locationName, isRestricted, pricingLocation }}
    >
      {children}
    </FixedPricingLocationContext.Provider>
  );
};

export const useFixedPricingLocationContext = () => {
  const context = useContext(FixedPricingLocationContext);
  if (!context) throw new Error("useFixedPricingLocationContext must be used within a FixedPricingLocationContextProvider");

  return context;
};
